import React from 'react';
import { useI18n, MONTH_AND_YEAR_FORMAT } from '@hydra/common/components/i18n';
import { values } from '@hydra/common/styles/breakpoints';
import { LineChart, XAxis, YAxis, CartesianGrid, Line, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { useReviewContext } from '@hydra/common/context/review';
import Dot from './components/Dot';

const GRAPH_WIDTH = 480;

export function CriteriumLineTimeChart ({
  chartData,
  chartOptions,
  hasLegend,
  colors,
  hasTooltip,
  frequency
}: {
  chartData: {
    data: {}[]
    charts: {
      dataKey: string,
      name: string
    }[]
  }
  chartOptions: {
    width: string
    height: number
  }
  hasLegend: boolean
  hasTooltip: boolean
  colors: string[]
  frequency?: 'day' | 'month'
}) {
  const { moment, locale } = useI18n();
  const { data, charts } = chartData;
  const { isPdf } = useReviewContext();

  const formatter = (value: number) => moment(value * 1000).format('mm:ss');

  const dateFormatter = (unixTime: string) => {
    if (frequency === 'month') {
      const string = unixTime.split(' ');
      const formattedValue = `${string[0]} 20${string[1]}`;
      return moment(new Date(formattedValue)).format(MONTH_AND_YEAR_FORMAT[locale]);
    }
    return unixTime;
  };

  const tickFormatter = (tick: string) => {
    if (frequency === 'month') {
      const string = tick.split(' ');
      const formattedValue = `${string[0]} '${string[1]}`;
      return formattedValue;
    }
    return tick;
  };

  const isDesktop = () => window.matchMedia(`(min-width: ${values.lg}px)`).matches;
  const graphWidth = isPdf ? GRAPH_WIDTH : '99%';

  return (
    // using width 99% to make container resize properly https://github.com/recharts/recharts/issues/172#issuecomment-307858843
    <ResponsiveContainer {...chartOptions} width={graphWidth}>
      <LineChart data={data}>
        <XAxis
          axisLine={false}
          dataKey={frequency || 'x'}
          tick={{ transform: 'translate(0, 5)', fontSize: 12 }}
          tickFormatter={tickFormatter}
          interval={frequency === 'month' && isDesktop() ? 0 : 'preserveEnd'}
          tickLine={false}
        />
        <YAxis
          axisLine={false}
          domain={[() => 0, (dataMax: number) => Math.ceil(dataMax / 60) * 60]}
          interval={0}
          tick={{ transform: 'translate(-5, 0)', fontSize: 12 }}
          tickLine={false}
          tickFormatter={formatter}
        />
        <CartesianGrid vertical={false} />
        {hasLegend && <Legend wrapperStyle={{ bottom: -10 }} />}
        {hasTooltip && <Tooltip formatter={formatter} labelFormatter={dateFormatter} />}
        {
          charts.map((chart, i) => (
            <Line
              key={chart.dataKey}
              isAnimationActive={false}
              dot={<Dot stroke={colors[i % colors.length]} />}
              dataKey={chart.dataKey}
              name={chart.name}
              type='monotone'
              stroke={colors[i % colors.length]}
              strokeWidth='2'
            />
          ))
        }
      </LineChart>
    </ResponsiveContainer>
  );
}

export default CriteriumLineTimeChart;
